// Per-browser record of the article tags and seats a visitor clicks, used to
// build the homepage "您可能关心的" section. Cookie-backed, mirroring the
// pollCookies.js helper (no backend, no login).
//
// The store is stamped with the deployment's pinned election id: interests
// recorded under a past election (e.g. Johor 2026) are invalidated wholesale
// when the pinned election changes, instead of leaking into the new one.

import { activeElectionId } from '../config/states';

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
const INTERESTS_COOKIE = 'ge15_interests';

const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const target = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.startsWith(target)) {
      return decodeURIComponent(part.slice(target.length));
    }
  }
  return null;
};

const writeCookie = (name, value, maxAgeSeconds = ONE_YEAR_SECONDS) => {
  if (typeof document === 'undefined') return;
  const encoded = encodeURIComponent(value);
  document.cookie = `${name}=${encoded}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
};

const emptyStore = () => ({ electionId: activeElectionId(), tags: {}, seats: {} });

const getStore = () => {
  const raw = readCookie(INTERESTS_COOKIE);
  if (!raw) return emptyStore();
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return emptyStore();
    // Interests from another election (or a legacy cookie with no electionId)
    // are stale — start fresh. The next record*() call overwrites the cookie.
    if (parsed.electionId !== activeElectionId()) return emptyStore();
    return {
      electionId: parsed.electionId,
      tags: parsed.tags && typeof parsed.tags === 'object' ? parsed.tags : {},
      seats: parsed.seats && typeof parsed.seats === 'object' ? parsed.seats : {},
    };
  } catch {
    return emptyStore();
  }
};

const saveStore = (store) => {
  try {
    writeCookie(INTERESTS_COOKIE, JSON.stringify(store));
  } catch {
    /* ignore quota / serialization errors */
  }
};

// General state-wide election tag opened by many entry points (e.g. every swing
// seat card) — too broad to be a useful personalization signal, so skip it.
// Both the traditional (as stored) and simplified (after tr()) forms are ignored.
const IGNORED_TAGS = new Set(['森州選舉', '森州选举']);

export const recordTag = (tag) => {
  if (!tag || typeof tag !== 'string') return;
  if (IGNORED_TAGS.has(tag)) return;
  const store = getStore();
  store.tags[tag] = (store.tags[tag] || 0) + 1;
  saveStore(store);
};

export const recordSeat = ({ slug, seatname, label } = {}) => {
  if (!slug || !seatname) return;
  const store = getStore();
  const key = `${slug}|${seatname}`;
  const existing = store.seats[key];
  store.seats[key] = {
    slug,
    seatname,
    label: label || (existing && existing.label) || seatname,
    count: (existing && existing.count ? existing.count : 0) + 1,
  };
  saveStore(store);
};

// Merged, click-count-ranked list of the visitor's top interests (tags + seats).
export const getTopInterests = (limit = 5) => {
  const store = getStore();
  const items = [];
  Object.keys(store.tags).forEach((tag) => {
    const count = store.tags[tag];
    if (count > 0) items.push({ type: 'tag', tag, count });
  });
  Object.keys(store.seats).forEach((key) => {
    const s = store.seats[key];
    if (s && s.count > 0) {
      items.push({ type: 'seat', slug: s.slug, seatname: s.seatname, label: s.label, count: s.count });
    }
  });
  // Sort by count desc; stable sort keeps insertion order for ties.
  items.sort((a, b) => b.count - a.count);
  return items.slice(0, limit);
};
